/*
 *  Copyright (c) 2023. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import React, { useEffect, useRef, useState } from "react";
import Paper from "@mui/material/Paper";
import { schufaCheckinStyles } from "./schufaCheckinStyles";
import * as loginActions from "../../../../../../store/login/actions";
import { useLocation, useNavigate } from "react-router-dom";
import { getLpByLocation } from "../../../../../../services/landingPagesList";
import Typography from "@mui/material/Typography";
import { FormProvider } from "../../../../../../provider/Form/FormProvider";
import SchufaCheckinForm from "./SchufaCheckinForm";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createResource, queryKeys, updateResource } from "../../../../../../services/reactQuery/reactQueryService";
import { Box, Stack, Switch } from "@mui/material";
import * as messagesActions from "../../../../../../store/messages/actions";
import { useCustomer } from "../../../../../../provider/Customer/CustomerProvider";
import { apiGet } from "../../../../../../services/apiClient";
import { getCaseLink } from "../../../../../../services/productService";
import moment from "moment/moment";
import { urltoFile } from "../../../../../../services/mediaObjects";
import { createMediaObject } from "../../../../../../services/reactQuery/reactQueryMediaObjectService";

export interface SchufaCheckinProps {
  idPrefix: string;
  partner: string;
}

export default function SchufaCheckin({ idPrefix, partner }: SchufaCheckinProps) {
  const [isLegacyCheckin, setIsLegacyCheckin] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [emailErrorText, setEmailErrorText] = useState("");
  const [formKey, setFormKey] = useState(0);
  const [submitErrorText, setSubmitErrorText] = useState("");
  const signaturePadRef = useRef<any>();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const createMutation = useMutation(createResource, {
    onSuccess: (data, variables) => queryClient.setQueryData(queryKeys.item(variables.uri, data.id), data),
  });
  const updateMutation = useMutation(updateResource);
  const createMediaObjectMutation = useMutation(createMediaObject);

  const { isLoggedIn, customer } = useCustomer();

  const legacyCheckinAllowed = process.env.REACT_APP_DEPLOYMENT !== "production";

  useEffect(() => {
    if (isLoggedIn) {
      setEmailErrorText("");
    }
  }, [isLoggedIn]);

  const initialValues = {
    email: isLoggedIn ? customer!.email : "",
    legalExpenseInsurance: "",
    telephone: "",
    mobileProviderOrganizationId: "",
    gender: "",
    givenName: "",
    familyName: "",
    streetAddress: "",
    postalCode: "",
    addressLocality: "",
    birthDate: "",
  };

  function getPreleadData(values: Record<string, any>) {
    const landingPage = getLpByLocation(location.pathname) || { keyword: location.pathname };

    return {
      initialProduct: "dataLeakContract",
      landingPage: landingPage.keyword,
      partner: partner,
      additionalData: {
        mobileProviderOrganizationId: parseInt(values.mobileProviderOrganizationId),
        affectedPhoneNumber: values.telephone,
        email: values.email.toLowerCase(),
      },
      legalExpenseInsurance: values.legalExpenseInsurance === "true",
    };
  }

  async function handleUninsuredSubmit(values: Record<string, any>) {
    await createMutation.mutateAsync({
      uri: "/preleads",
      data: getPreleadData(values),
    });
    messagesActions.addMessage({
      type: "success",
      text: "Vielen Dank für das Interesse an unserem Produkt! Wir melden uns bei dir, sobald wir den Service anbieten können.",
    });
  }

  async function handleInsuredSubmit(values: Record<string, any>) {
    if (!legacyCheckinAllowed) {
      return;
    }

    if (signaturePadRef.current.signaturePadInstance.isEmpty()) {
      setSubmitErrorText("Bitte unterschreibe die Vollmacht");
      return;
    }
    const file = await urltoFile(signaturePadRef.current.signaturePadInstance.toDataURL("image/svg+xml"), "signature.svg", "image/svg+xml");

    if (!isLoggedIn) {
      const emailCheck = await loginActions.checkEmail(values.email.toLowerCase());
      if (emailCheck.hasError) {
        setSubmitErrorText("Bitte überprüfe deine E-Mail-Adresse oben");
        setIsLoading(false);
        setEmailErrorText(emailCheck.errorText);
        return;
      }
    }
    setSubmitErrorText("");

    const landingPage = getLpByLocation(location.pathname) || { keyword: location.pathname };

    const data = {
      email: values.email.toLowerCase(),
      gclid: localStorage.getItem("gclid"),
      prelead: getPreleadData(values),
      telephone: values.telephone,
      person: {
        gender: values.gender,
        givenName: values.givenName,
        familyName: values.familyName,
        birthDate: values.birthDate.format("DD.MM.YYYY"),
      },
    };

    const needsRegister = !isLoggedIn;

    if (needsRegister) {
      await loginActions.register(data);
    }
    const product = await createMutation.mutateAsync({
      uri: "/data_leak_contracts",
      data: {
        acquisitionPartner: partner,
        affiliateCode: localStorage.getItem("gclid"),
        legalExpenseInsurance: values.legalExpenseInsurance === "true",
        landingPage: landingPage.keyword,
        mobileProviderOrganizationId: parseInt(values.mobileProviderOrganizationId),
        affectedPhoneNumber: values.telephone,
      },
    });

    //todo improve with promise.all
    if (needsRegister) {
      //manual fetch because customer provider isnt fast enough with delivering the new customer
      const fetchedCustomer = (await apiGet("/customers"))[0];
      if (fetchedCustomer) {
        await updateMutation.mutateAsync({
          uri: "/addresses",
          id: fetchedCustomer.person.residenceAddress.id,
          data: {
            streetAddress: values.streetAddress,
            postalCode: values.postalCode,
            addressLocality: values.addressLocality,
          },
        });
      }
    }

    await updateMutation.mutateAsync({
      uri: "/addresses",
      id: product.client.residenceAddress.id,
      data: {
        streetAddress: values.streetAddress,
        postalCode: values.postalCode,
        addressLocality: values.addressLocality,
      },
    });

    const uploadedSignature = await createMediaObjectMutation.mutateAsync({
      data: {
        file: file,
        description: "Unterschrift",
        fieldname: "signature",
        product: product.name,
        productId: product.id,
      },
    });

    await updateMutation.mutateAsync({
      uri: "/people",
      id: product.client.id,
      data: {
        signature: `media_objects/${uploadedSignature.id}`,
        gender: values.gender,
        givenName: values.givenName,
        familyName: values.familyName,
        birthDate: values.birthDate.format("DD.MM.YYYY"),
      },
    });

    await updateMutation.mutateAsync({
      uri: "/data_leak_contracts",
      id: product.id,
      data: {
        powerOfAttorney: moment().format("DD.MM.YYYY"),
      },
    });
    await queryClient.invalidateQueries(queryKeys.collection("/customers"));

    setFormKey(formKey + 1);
    navigate(getCaseLink(product) + "/antrag");
  }

  const handleSubmit = async ({ values }: { values: Record<string, any> }) => {
    setIsLoading(true);

    if (values.legalExpenseInsurance === "true") {
      await handleInsuredSubmit(values);
    } else {
      await handleUninsuredSubmit(values);
      setFormKey(formKey + 1);
    }
    setIsLoading(false);
  };
  return (
    <Paper elevation={4} sx={schufaCheckinStyles.formPaper}>
      <Box id={idPrefix + "-checkin"} sx={schufaCheckinStyles.scrollToAnchor} />
      {legacyCheckinAllowed && (
        <Stack direction={"row"} alignItems={"center"}>
          <Switch value={isLegacyCheckin} onChange={() => setIsLegacyCheckin((prevState) => !prevState)} />
          <Typography>Legacy Mode</Typography>
        </Stack>
      )}
      <Typography variant={"h4"} sx={schufaCheckinStyles.heading}>
        {/*{isLoggedIn ? "Du bist angemeldet. Jetzt Fall prüfen lassen!" : "Wir prüfen deinen Anspruch auf 1.500 € kostenlos!"}*/}
        Bleibe auf dem Laufenden!
      </Typography>
      <Typography variant={"subtitle2"} sx={schufaCheckinStyles.heading}>
        Du möchtest benachrichtigt werden, sobald wir wieder neue Mandate annehmen? Hinterlasse einfach hier deine E-Mail-Adresse und du wirst automatisch von
        uns informiert.
      </Typography>
      <FormProvider initialValues={initialValues} submitAction={handleSubmit} key={formKey}>
        <SchufaCheckinForm
          isLoading={isLoading}
          externalError={submitErrorText}
          emailErrorText={emailErrorText}
          setEmailErrorText={setEmailErrorText}
          signaturePadRef={signaturePadRef}
          isLegacyMode={isLegacyCheckin}
        />
      </FormProvider>
    </Paper>
  );
}
